// tslint:disable-next-line:max-line-length
import {
  IIntegratedCare,
  IService,
  IServiceOrder,
  ServiceCostEstimateApprovalInfo,
  ServiceCostEstimateRejectionInfo,
  ServiceCostEstimateRequestStatus,
  ServiceOrderStatus,
  TransmissionStatus,
} from '@alberta/konexi-shared';
import { DoctorDB, PatientDB, ServiceDB } from '@common/repository/databases';
import { createObject } from '@common/tracking/object-creator';
import { ignore, removeTime, tracked } from '@common/tracking/tracker';
import { BaseViewModel } from '@common/viewmodel/base-view-model';
import { QueryService } from '@services/query/query.service';
import { DoctorDto } from 'src/app/shared/models/institution/doctor-dto.model';
import { v4 } from 'uuid';

import { SQLiteQueryBuilder } from '@common/storage/sqlite-query-builder';
import { PatientDto } from 'src/app/shared/models/patient/patient-dto.model';
import { ServiceOrderedServiceLineViewModel } from './service-ordered-service-line-view-model';

export class ServiceOrderViewModel extends BaseViewModel implements IServiceOrder {
  serviceCostEstimateApprovalOrRejection: ServiceCostEstimateRejectionInfo | ServiceCostEstimateApprovalInfo;
  hasFollowUpOrder: boolean;
  serviceCostEstimateCreationInfo: { createdAt: string; createdBy: string };
  serviceCostEstimateRequestStatus: ServiceCostEstimateRequestStatus;
  @tracked({ factory: createObject(ServiceOrderedServiceLineViewModel) })
  orderedServiceLines: ServiceOrderedServiceLineViewModel[];
  @removeTime()
  startDate: Date;
  @removeTime()
  endDate: Date;
  therapyId: number;
  therapyTypeId: number;
  woundLocation: number;
  additionalLocations?: string[];
  patientId: string;
  doctorId: string;
  payerInstitutionCode: string;
  isInitialCare: boolean;
  annotation: string;
  integratedCareId: string;
  status: ServiceOrderStatus;
  medicalOrderRequestStatus: TransmissionStatus;
  regionId: string;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
  transmittedAt: string;
  @removeTime()
  medicalOrderDate: string;

  @ignore()
  doctorName = 'Unbekannt';
  @ignore()
  integratedCare: IIntegratedCare;

  public async initializeNew(integratedCare: IIntegratedCare, queryService: QueryService): Promise<void> {
    this._id = v4();
    this.orderedServiceLines = [];
    this.startDate = null;
    this.endDate = null;
    this.therapyId = integratedCare.therapyId;
    this.therapyTypeId = integratedCare.therapyTypeId;
    this.woundLocation = integratedCare.location;
    this.patientId = integratedCare.patientId;
    this.doctorId = integratedCare.doctorId;
    this.isInitialCare = integratedCare.isInitialCare ?? false;
    this.additionalLocations = integratedCare.additionalLocations;
    this.annotation = '';
    this.integratedCareId = integratedCare._id;
    this.integratedCare = integratedCare;
    this.status = ServiceOrderStatus.Created;
    this.regionId = integratedCare.regionId;
    this.createdAt = new Date();
    this.createdBy = '';
    this.updatedAt = new Date();
    this.updatedBy = '';
    this.timestamp = new Date();
    this.archived = false;

    const [services] = await Promise.all([
      this.loadServicesOfServiceLines(integratedCare.proposedServiceLines, queryService),
      this.setPayerInstitutionCode(queryService),
      this.setDoctorName(queryService),
    ]);
    this.addServiceLinesFromIntegratedCare(integratedCare, true, services);
  }

  public async initializeExisting(
    integratedCare: IIntegratedCare,
    queryService: QueryService,
    loadMissingServiceLinesFromIntegratedCare?: boolean
  ): Promise<void> {
    await this.populateExistingServiceLines(queryService);
    if (loadMissingServiceLinesFromIntegratedCare) {
      const services = await this.loadServicesOfServiceLines(integratedCare.proposedServiceLines, queryService);
      await this.addServiceLinesFromIntegratedCare(integratedCare, false, services);
    }
    await Promise.all([this.setDoctorName(queryService), this.setPayerInstitutionCode(queryService)]);
  }

  private async loadServicesOfServiceLines(
    serviceLines: { serviceId: string }[],
    queryService: QueryService
  ): Promise<IService[]> {
    if (!serviceLines || serviceLines.length === 0) {
      return [];
    }

    const serviceIds = [...new Set(serviceLines.map(serviceLine => serviceLine.serviceId))];
    return this.loadServices(serviceIds, queryService);
  }

  private async loadServices(ids: string[], queryService: QueryService): Promise<IService[]> {
    const services = await queryService.raw(
      {
        sql: new SQLiteQueryBuilder()
          .selectAll()
          .fromFts(ServiceDB)
          .where('[service_fts].id', 'IN', ids)
          .innerJoin(ServiceDB, 'id', '=', 'key')
          .asString(),
        aggregation: [
          {
            $match: {
              _id: { $in: ids },
            },
          },
        ],
      },
      ServiceDB
    );
    return (services ?? []) as IService[];
  }

  private async populateExistingServiceLines(queryService: QueryService) {
    const missingServiceIds = Array.from(new Set(this.orderedServiceLines.map(serviceLine => serviceLine.serviceId)));
    const services = await this.loadServices(missingServiceIds, queryService);
    for (const orderedServiceLine of this.orderedServiceLines) {
      orderedServiceLine.active = true;
      const service = services.find(service => service._id === orderedServiceLine.serviceId);
      orderedServiceLine.service = service;
    }
  }

  private async addServiceLinesFromIntegratedCare(
    integratedCare: IIntegratedCare,
    setActive: boolean,
    services: IService[]
  ) {
    for (const proposedServiceLine of integratedCare.proposedServiceLines) {
      if (
        this.orderedServiceLines.find(
          orderedServiceLine => orderedServiceLine.service._id === proposedServiceLine.serviceId
        )
      ) {
        continue;
      }
      const service = services.find(service => service._id === proposedServiceLine.serviceId);

      const newServiceLine = new ServiceOrderedServiceLineViewModel();
      newServiceLine.initialize(proposedServiceLine, service);
      // explicitly check for false. If the flag is undefined or true it means automatic appointment creation
      if (newServiceLine?.automaticAppointmentCreation === false) {
        newServiceLine.quantity = 0;
      }
      newServiceLine.active = setActive;
      this.orderedServiceLines.push(newServiceLine);
    }
  }

  private async setDoctorName(queryService: QueryService) {
    if (!this.doctorId) {
      return;
    }
    const doctor = await queryService.get<DoctorDto>(this.doctorId, DoctorDB);
    if (doctor) {
      this.doctorName = `${doctor.firstName} ${doctor.lastName}`;
    }
  }
  private async setPayerInstitutionCode(queryService: QueryService) {
    if (!this.patientId) {
      return;
    }
    const patient = await queryService.get<PatientDto>(this.patientId, PatientDB);
    if (patient) {
      this.payerInstitutionCode = patient.payerInstitutionCode;
    }
  }
}
